import React, { useState, useRef, useEffect } from "react";
import { supabase } from "../../supabaseClient";
import "bootstrap-icons/font/bootstrap-icons.css";

/**
 * Reusable MicButton component for speech-to-text functionality.
 * Can be used with any input/textarea field.
 * 
 * @param {Object} props
 * @param {Function} props.onTranscript - Callback when audio is transcribed (receives text)
 * @param {Function} [props.onDirectSend] - Optional callback for direct send (receives text)
 * @param {boolean} [props.disabled] - Disable the button
 * @param {string} [props.size] - Button size: 'sm' (32px) or 'md' (40px, default)
 * @param {string} [props.apiUrl] - API URL for speech-to-text (defaults to REACT_APP_ZOOMEY_API_URL)
 */
const MicButton = ({
  onTranscript,
  onDirectSend,
  disabled = false,
  size = "md",
  apiUrl = process.env.REACT_APP_ZOOMEY_API_URL,
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const mediaStreamRef = useRef(null);
  const intervalRef = useRef(null);
  const isCancelledRef = useRef(false);
  const actionRef = useRef("addToText"); // 'addToText' or 'directSend'

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Audio to text transcription
  const audioToText = async (audioBlob) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error("No valid session found");
      }

      const formData = new FormData();
      formData.append("file", audioBlob, "recording.mp3");

      const response = await fetch(`${apiUrl}/speech-to-text`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Transcription failed");
      }

      const data = await response.json();
      return data.text || "";
    } catch (error) {
      console.error("[MicButton] Audio transcription error:", error);
      return "";
    }
  };

  // Start recording
  const handleStartRecording = async () => {
    try {
      audioChunksRef.current = [];
      isCancelledRef.current = false;
      actionRef.current = "addToText";

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream);
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        // If cancelled, don't process
        if (isCancelledRef.current) {
          if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(track => track.stop());
          }
          return;
        }

        // Create audio blob and transcribe
        if (audioChunksRef.current.length > 0) {
          setIsProcessing(true);
          const audioBlob = new Blob(audioChunksRef.current, { type: "audio/mp3" });
          const transcribedText = await audioToText(audioBlob);

          if (transcribedText) {
            if (actionRef.current === "directSend" && onDirectSend) {
              onDirectSend(transcribedText);
            } else if (onTranscript) {
              onTranscript(transcribedText);
            }
          }
          setIsProcessing(false);
        }

        // Cleanup
        if (mediaStreamRef.current) {
          mediaStreamRef.current.getTracks().forEach(track => track.stop());
        }
      };

      mediaRecorder.start();
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      intervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (error) {
      console.error("[MicButton] Microphone access error:", error);
      alert("Could not access microphone. Please check permissions.");
    }
  };

  // Stop recording and add to text
  const handleStopAndAddToText = () => {
    if (mediaRecorderRef.current && isRecording) {
      actionRef.current = "addToText";
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  };

  // Stop recording and send directly
  const handleStopAndSend = () => {
    if (mediaRecorderRef.current && isRecording) {
      actionRef.current = "directSend";
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  };

  // Cancel recording
  const handleCancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      isCancelledRef.current = true;
      audioChunksRef.current = [];
      mediaRecorderRef.current.stop();

      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
      }

      setIsRecording(false);
      setRecordingTime(0);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  };

  // Button sizes
  const buttonSize = size === "sm" ? "32px" : "40px";
  const iconSize = size === "sm" ? "16px" : "20px";

  // Common button style
  const buttonStyle = {
    borderRadius: "8px",
    border: "1px solid var(--border-color)",
    background: "transparent",
    color: "var(--text)",
    width: buttonSize,
    height: buttonSize,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    flexShrink: 0,
  };

  // Processing state
  if (isProcessing) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <div style={{
          width: "16px",
          height: "16px",
          border: "2px solid var(--border-color)",
          borderTop: "2px solid var(--teal-bg, #14b8a6)",
          borderRadius: "50%",
          animation: "micButtonSpin 0.5s linear infinite",
        }} />
        <span style={{ fontSize: "12px", color: "var(--text)", opacity: 0.7 }}>
          Processing...
        </span>
        <style>{`
          @keyframes micButtonSpin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Recording state
  if (isRecording) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        {/* Add to Text button */}
        <button
          type="button"
          onClick={handleStopAndAddToText}
          title="Add to text"
          style={buttonStyle}
        >
          <i className="bi bi-journal-text" style={{ fontSize: iconSize, opacity: 0.8 }}></i>
        </button>

        {/* Cancel button */}
        <button
          type="button"
          onClick={handleCancelRecording}
          title="Cancel recording"
          style={{
            ...buttonStyle,
            background: "var(--background)",
          }}
        >
          <i className="bi bi-x-lg" style={{ fontSize: size === "sm" ? "12px" : "14px", color: "#ef4444", fontWeight: "bold" }}></i>
        </button>

        {/* Stop and Send button (only if onDirectSend is provided) */}
        {onDirectSend && (
          <button
            type="button"
            onClick={handleStopAndSend}
            title="Stop and send"
            style={{
              ...buttonStyle,
              background: "#ef4444",
              border: "none",
              color: "white",
              borderRadius: "50%",
            }}
          >
            <i className="bi bi-stop-fill" style={{ fontSize: iconSize }}></i>
          </button>
        )}

        {/* Timer */}
        <span style={{ 
          fontSize: size === "sm" ? "12px" : "14px", 
          color: "var(--text)",
          minWidth: "30px",
        }}>
          {recordingTime}s
        </span>
      </div>
    );
  }

  // Idle state - mic button
  return (
    <button
      type="button"
      onClick={handleStartRecording}
      title="Start recording"
      disabled={disabled}
      style={buttonStyle}
    >
      <i className="bi bi-mic" style={{ fontSize: iconSize, opacity: 0.6 }}></i>
    </button>
  );
};

export default MicButton;
